import * as React from "react";
import { useTranslation } from "react-i18next";

import { Box, Button, Checkbox, Dialog, Tooltip, Typography } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import axios from "axios";

import useSnackbarAlert from "../../../utils/snackbarHandlers";
import { COLORS } from "./constants";
import InventoryTabs from "./InventoryTabs";
import ScanHelpTooltip from "./ScanHelpTooltip";
import ConfirmActivateModal from "./ConfirmActivateModal";

interface SaleDayDetailProps {
  apiBaseURL: string;
  shopId: any;
  socket: any;
  currencySymbol: string;
  initialDate: string;
  onBackToCalendar: () => void;
  onConfirmClick?: () => void;
  onActivateClick?: () => void;
  onSettlementClick?: () => void;
}

interface SalesRow {
  inventoryId: number;
  gameNumber: string;
  packNumber: string;
  name: string;
  imageUrl: string;
  screenNumber: number;
  ticketPosition: number;
  slotNumber: number;
  startNumber: number;
  endNumber: number;
  packSize: number;
  ticketValue: string;
  ticketsSold: number;
  amount: number;
  isSoldOut: boolean;
  edited: boolean;
  // a pack returned while active - render as a closed (red) row, inputs disabled
  isReturned?: boolean;
  returnedDate?: string;
}

interface RowEdit {
  endNumber?: string;
  slotNumber?: string;
  isSoldOut?: boolean;
}

const MONTHS_FULL = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const formatLong = (d: string) => {
  const [y, m, day] = d.split("-");
  return `${MONTHS_FULL[parseInt(m) - 1]}/${day}/${y}`;
};
const formatSlash = (d: string) => {
  if (!d) return "";
  const [y, m, day] = d.split("-");
  return `${m}/${day}/${y}`;
};
const addDays = (d: string, n: number) => {
  const dt = new Date(`${d}T00:00:00Z`);
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().split("T")[0];
};

// A returned pack can be restored for 3 days from its return date (UI gate; the
// backend is the authority). returnedDate is a "YYYY-MM-DD" string.
const withinRestoreWindow = (returnedDate?: string): boolean => {
  if (!returnedDate) return false;
  const diff = Math.floor((Date.now() - new Date(`${returnedDate}T00:00:00`).getTime()) / 86400000);
  return diff >= 0 && diff <= 3;
};

const SaleDayDetail: React.FC<SaleDayDetailProps> = ({
  apiBaseURL,
  shopId,
  socket,
  currencySymbol,
  initialDate,
  onBackToCalendar,
  onConfirmClick,
  onActivateClick,
  onSettlementClick,
}) => {
  const { t } = useTranslation("translation");
  const { showErrorMessage, showSuccessMessage } = useSnackbarAlert();

  const [date, setDate] = React.useState<string>(initialDate);
  const [rows, setRows] = React.useState<SalesRow[]>([]);
  const [meta, setMeta] = React.useState<any>({});
  const [edits, setEdits] = React.useState<Record<number, RowEdit>>({});
  const [loading, setLoading] = React.useState<boolean>(false);
  const [saving, setSaving] = React.useState<boolean>(false);
  const [refreshToken, setRefreshToken] = React.useState<number>(0);
  const refresh = () => setRefreshToken(Math.random());

  const [confirmActivateOpen, setConfirmActivateOpen] = React.useState<boolean>(false);
  const [endDayOpen, setEndDayOpen] = React.useState<boolean>(false);
  const [restoreTarget, setRestoreTarget] = React.useState<SalesRow | null>(null); // returned row pending restore confirm
  const [restoring, setRestoring] = React.useState<boolean>(false);

  // quick entry bar (scan / game / pack -> locate the active row)
  const [scanCode, setScanCode] = React.useState<string>("");
  const [gameNumber, setGameNumber] = React.useState<string>("");
  const [packNumber, setPackNumber] = React.useState<string>("");
  const [highlightId, setHighlightId] = React.useState<number | null>(null);
  const endRefs = React.useRef<Record<number, HTMLInputElement | null>>({});

  const canEdit = !!meta.canEdit;
  const isCurrent = !!meta.isCurrentBusinessDate;

  // ---- fetch the day ----
  React.useEffect(() => {
    setLoading(true);
    axios
      .post(`${apiBaseURL}/api/inventory-v2/sales/day`, { userId: shopId, date })
      .then((res: any) => {
        setRows(res?.data?.rows ?? []);
        setMeta(res?.data ?? {});
        setEdits({});
      })
      .catch(() => showErrorMessage(t("somethingWentWrong", "Something went wrong")))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseURL, shopId, date, refreshToken]);

  // ---- socket acks for the save ----
  React.useEffect(() => {
    if (!socket) return;
    const onSaved = () => {
      setSaving(false);
      showSuccessMessage(t("salesSavedMsg", "Sales updated successfully"));
      refresh();
    };
    const onSaveError = (response: any) => {
      setSaving(false);
      showErrorMessage(response?.message ?? t("somethingWentWrong", "Something went wrong"));
    };
    const onRestored = () => {
      setRestoring(false);
      setRestoreTarget(null);
      showSuccessMessage(t("restoreSuccessMsg", "Pack restored successfully"));
      refresh();
    };
    const onRestoreError = (response: any) => {
      setRestoring(false);
      showErrorMessage(response?.message ?? t("somethingWentWrong", "Something went wrong"));
    };
    socket.on("saveInventoryV2SalesResponse", onSaved);
    socket.on("saveInventoryV2SalesError", onSaveError);
    socket.on("restoreInventoryV2Response", onRestored);
    socket.on("restoreInventoryV2Error", onRestoreError);
    return () => {
      socket.off("saveInventoryV2SalesResponse", onSaved);
      socket.off("saveInventoryV2SalesError", onSaveError);
      socket.off("restoreInventoryV2Response", onRestored);
      socket.off("restoreInventoryV2Error", onRestoreError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  const handleRestore = () => {
    if (!restoreTarget) return;
    if (!socket) return showErrorMessage(t("socketNotReady", "Connection not ready, please retry"));
    setRestoring(true);
    socket.emit("restoreInventoryV2", { shopId, id: restoreTarget.inventoryId });
  };

  // ---- per-row derived values (apply local edits for a live preview) ----
  const rowSoldOut = (r: SalesRow) => {
    const e = edits[r.inventoryId]?.isSoldOut;
    return e !== undefined ? e : r.isSoldOut;
  };
  const rowEndValue = (r: SalesRow): string => {
    const e = edits[r.inventoryId]?.endNumber;
    if (e !== undefined) return e;
    return String(r.endNumber);
  };
  const rowSlotValue = (r: SalesRow): string => {
    const e = edits[r.inventoryId]?.slotNumber;
    if (e !== undefined) return e;
    return String(r.slotNumber);
  };
  const rowSold = (r: SalesRow) => {
    const end = rowSoldOut(r) ? r.packSize : (parseInt(rowEndValue(r)) || 0);
    return Math.max(0, end - r.startNumber);
  };
  const rowAmount = (r: SalesRow) => rowSold(r) * (parseFloat(r.ticketValue) || 0);
  const isDirty = (r: SalesRow) => edits[r.inventoryId] !== undefined;
  const isEditedRow = (r: SalesRow) => r.edited || isDirty(r);

  const setEdit = (inventoryId: number, field: keyof RowEdit, value: any) => {
    setEdits((prev) => ({ ...prev, [inventoryId]: { ...prev[inventoryId], [field]: value } }));
  };

  const money = (v: number) => `${currencySymbol}${Number.isInteger(v) ? v : v.toFixed(2)}`;

  // ---- totals (live) ----
  const totalTicketsSold = rows.reduce((s, r) => s + rowSold(r), 0);
  const totalAmount = rows.reduce((s, r) => s + rowAmount(r), 0);
  const gamesEntered = rows.filter((r) => isEditedRow(r)).length;

  // ---- save ----
  const handleSave = () => {
    const dirtyIds = Object.keys(edits).map((k) => parseInt(k));
    if (!dirtyIds.length) return showErrorMessage(t("nothingToSave", "There are no changes to save"));
    if (!socket) return showErrorMessage(t("socketNotReady", "Connection not ready, please retry"));
    const payloadRows = dirtyIds.map((inventoryId) => ({ inventoryId, ...edits[inventoryId] }));
    setSaving(true);
    socket.emit("saveInventoryV2Sales", { shopId, date, rows: payloadRows });
  };

  // ---- end day ----
  const handleEndDay = () => {
    axios
      .post(`${apiBaseURL}/api/inventory-v2/sales/endDay`, { userId: shopId, date })
      .then((res: any) => {
        showSuccessMessage(t("dayEndedMsg", "Day ended successfully"));
        setEndDayOpen(false);
        setDate(res?.data?.nextDate ?? date); // move to the new current business date
      })
      .catch((error: any) => {
        setEndDayOpen(false);
        showErrorMessage(error?.response?.data?.message ?? t("somethingWentWrong", "Something went wrong"));
      });
  };

  // ---- previous day ----
  const goPreviousDay = () => {
    if (!meta.canGoPrevious) return;
    setDate((d) => addDays(d, -1));
  };

  // ---- quick entry: locate an active ticket's row ----
  const onScanChange = (value: string) => {
    setScanCode(value);
    if (value.length >= 11) {
      setGameNumber(value.substring(0, 4));
      setPackNumber(value.substring(4, 11));
    }
  };
  const locateRow = (game: string, pack: string) => {
    const row = rows.find((r) => r.gameNumber === game && (!pack || r.packNumber === pack));
    if (!row) {
      return showErrorMessage(t("onlyActivatedInSales", "Only activated tickets can be recorded here"));
    }
    setHighlightId(row.inventoryId);
    const el = endRefs.current[row.inventoryId];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.focus();
    }
    setScanCode("");
    setGameNumber("");
    setPackNumber("");
  };
  const submitLocate = () => {
    const game = (gameNumber || scanCode.substring(0, 4)).trim();
    const pack = (packNumber || scanCode.substring(4, 11)).trim();
    locateRow(game, pack);
  };
  const onEntryKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    submitLocate();
  };

  // ---- print (table only) ----
  const printTable = () => {
    const head = ["Image", "Name", "Game #", "Pack #", "Pack Size #", "Slot #", "Start #", "End #", "Sold Out", "Tickets Sold", "Tickets Value", "Amount"];
    const body = rows
      .map((r) => {
        const cells = [
          r.imageUrl ? `<img src="${r.imageUrl}" style="height:34px"/>` : "",
          r.name, r.gameNumber, r.packNumber, r.packSize, rowSlotValue(r), r.startNumber,
          rowSoldOut(r) ? r.packSize : rowEndValue(r), rowSoldOut(r) ? "Yes" : "No",
          rowSold(r), money(parseFloat(r.ticketValue) || 0), money(rowAmount(r)),
        ];
        return `<tr>${cells.map((c) => `<td>${c}</td>`).join("")}</tr>`;
      })
      .join("");
    const html = `<!doctype html><html><head><title>Sales - ${formatLong(date)}</title>
      <style>
        body{font-family:Arial,Helvetica,sans-serif;padding:24px;color:#222}
        h2{margin:0 0 4px} .sub{color:#666;margin-bottom:16px}
        table{border-collapse:collapse;width:100%} th,td{border:1px solid #bbb;padding:6px 8px;text-align:center;font-size:13px}
        th{background:#f17400;color:#fff} tfoot td{font-weight:bold;background:#f5f5f5}
      </style></head><body>
      <h2>Sales Report</h2>
      <div class="sub">Sale Date: ${formatLong(date)} &nbsp;|&nbsp; Business Date: ${formatSlash(meta.currentBusinessDate)}</div>
      <table><thead><tr>${head.map((h) => `<th>${h}</th>`).join("")}</tr></thead>
      <tbody>${body}</tbody>
      <tfoot><tr><td colspan="9">Total</td><td>${totalTicketsSold}</td><td></td><td>${money(totalAmount)}</td></tr></tfoot>
      </table></body></html>`;
    const w = window.open("", "_blank", "width=1100,height=800");
    if (!w) return showErrorMessage(t("popupBlocked", "Please allow pop-ups to print"));
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
  };

  // ---- styles ----
  const thSx = { fontSize: "15px", fontWeight: 700, color: "#000", textAlign: "center", py: "10px", px: "6px", borderBottom: `2px solid ${COLORS.border}` } as const;
  const tdSx = { fontSize: "15px", color: "#222", textAlign: "center", py: "6px", px: "6px" } as const;
  const cellInputSx = {
    width: "64px", height: "32px", borderRadius: "4px", border: `1px solid ${COLORS.border}`,
    textAlign: "center", fontSize: "15px", outline: "none", backgroundColor: "#fff",
  } as const;
  const topBtn = (bg: string) => ({
    backgroundColor: bg, color: "#fff", textTransform: "none", borderRadius: "6px", height: "40px",
    px: 2.5, fontSize: "16px", fontWeight: 600, "&:hover": { backgroundColor: bg, opacity: 0.9 },
    "&.Mui-disabled": { backgroundColor: "#bdbdbd", color: "#fff" },
  });

  return (
    <Box p={2}>
      {/* Header: back arrow + Sale Date + tabs */}
      <Box display='flex' alignItems='center' justifyContent='space-between' flexWrap='wrap' gap='12px' mb={2} sx={{ borderBottom: "1px solid #e0e0e0", pb: 1 }}>
        <Box display='flex' alignItems='center' gap='12px'>
          <Box component='span' onClick={onBackToCalendar} sx={{ cursor: "pointer", fontSize: "26px", lineHeight: 1, color: "#000" }} aria-label={t("back", "Back")}>
            ←
          </Box>
          <Typography sx={{ fontSize: "26px", fontWeight: 700, color: "#000" }}>
            {t("saleDateLabel", "Sale Date")} : {formatLong(date)}
          </Typography>
        </Box>
        <InventoryTabs active='sales' onConfirmClick={onConfirmClick} onActivateClick={onActivateClick} onSalesClick={onBackToCalendar} onSettlementClick={onSettlementClick} />
      </Box>

      {/* Control bar: Previous Day | entry panel | End Day (all vertically centered) */}
      <Box display='flex' alignItems='center' gap='14px' flexWrap='wrap' mb={1.5}>
        <Button onClick={goPreviousDay} disabled={!meta.canGoPrevious} disableElevation sx={topBtn("#000")}>
          ← {t("previousDay", "Previous Day")}
        </Button>

        <Box
          display='flex'
          alignItems='flex-end'
          justifyContent='space-evenly'
          gap='16px'
          flexWrap='wrap'
          sx={{ flex: 1, backgroundColor: COLORS.panelBg, border: "0.25px solid #434343", p: 1.5, borderRadius: "4px" }}
        >
          <Box sx={{ flex: "1.6 1 220px" }}>
            <Typography sx={{ fontSize: "15px", color: "#434343", mb: "4px" }}>{t("scanCodeLabel", "Scan Code")}</Typography>
            <Box display='flex' alignItems='center' gap='8px'>
              <Box component='input' value={scanCode} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onScanChange(e.target.value)} onKeyDown={onEntryKeyDown} placeholder={t("scanCodePlaceholder", "Scan / enter barcode")} sx={{ flex: 1, minWidth: 0, height: "36px", borderRadius: "4px", border: "1px solid #434343", px: "10px", fontSize: "16px", outline: "none", backgroundColor: "#fff" }} />
              <ScanHelpTooltip />
            </Box>
          </Box>
          <Typography sx={{ fontSize: "16px", color: "#434343", pb: "8px" }}>{t("or", "OR")}</Typography>
          <Box sx={{ flex: "1 1 160px" }}>
            <Typography sx={{ fontSize: "15px", color: "#434343", mb: "4px" }}>{t("gameNumberLabel", "Game Number")}</Typography>
            <Box component='input' value={gameNumber} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGameNumber(e.target.value)} onKeyDown={onEntryKeyDown} sx={{ width: "100%", height: "36px", borderRadius: "4px", border: "1px solid #434343", px: "10px", fontSize: "16px", outline: "none", backgroundColor: "#fff", boxSizing: "border-box" }} />
          </Box>
          <Box sx={{ flex: "1 1 160px" }}>
            <Typography sx={{ fontSize: "15px", color: "#434343", mb: "4px" }}>{t("packNumberLabel", "Pack Number")}</Typography>
            <Box component='input' value={packNumber} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPackNumber(e.target.value)} onKeyDown={onEntryKeyDown} sx={{ width: "100%", height: "36px", borderRadius: "4px", border: "1px solid #434343", px: "10px", fontSize: "16px", outline: "none", backgroundColor: "#fff", boxSizing: "border-box" }} />
          </Box>
          <Button onClick={submitLocate} disableElevation sx={topBtn(COLORS.blue)}>
            {t("enterButton", "Enter")}
          </Button>
        </Box>

        <Button onClick={() => setEndDayOpen(true)} disabled={!meta.canEndDay} disableElevation sx={topBtn(COLORS.red)}>
          {t("endDay", "End Day")}
        </Button>
      </Box>

      {/* Confirm & Activate button (right aligned) */}
      <Box display='flex' justifyContent='flex-end' mb={1.5}>
        <Button onClick={() => setConfirmActivateOpen(true)} disableElevation sx={topBtn(COLORS.navy)}>
          {t("confirmActivateButton", "Confirm & Activate")}
        </Button>
      </Box>

      {/* Status badges + print */}
      <Box display='flex' alignItems='center' gap='16px' mb={1}>
        <Box display='flex' alignItems='center' gap='6px'>
          <Typography sx={{ fontSize: "16px", fontWeight: 700 }}>{t("gamesEntered", "Games Entered")}:</Typography>
          <Box sx={{ backgroundColor: COLORS.red, color: "#fff", borderRadius: "4px", px: "10px", py: "2px", fontWeight: 700, fontSize: "15px" }}>{gamesEntered}</Box>
        </Box>
        <Box display='flex' alignItems='center' gap='6px'>
          <Typography sx={{ fontSize: "16px", fontWeight: 700 }}>{t("ticketsSoldBadge", "Tickets Sold")}:</Typography>
          <Box sx={{ backgroundColor: COLORS.green, color: "#000", borderRadius: "4px", px: "10px", py: "2px", fontWeight: 700, fontSize: "15px" }}>{money(totalAmount)}</Box>
        </Box>
        <Box flex={1} />
        <PrintIcon sx={{ cursor: "pointer", color: "#434343", fontSize: 28 }} onClick={printTable} />
      </Box>

      {/* Sales table */}
      <Box sx={{ width: "100%", overflowX: "auto", border: `1px solid ${COLORS.border}`, borderRadius: "4px", opacity: loading ? 0.6 : 1 }}>
        <Box component='table' sx={{ width: "100%", borderCollapse: "collapse", minWidth: "1000px" }}>
          <Box component='thead'>
            <Box component='tr' sx={{ backgroundColor: "#fff" }}>
              {["Image", "Name", "Game #", "Pack #", "Pack Size #", "Slot #", "Start #", "End #", "Sold Out?", "Tickets Sold", "Tickets Value", "Amount"].map((h) => (
                <Box component='th' key={h} sx={thSx}>{h}</Box>
              ))}
            </Box>
          </Box>
          <Box component='tbody'>
            {rows.map((r, idx) => {
              const edited = isEditedRow(r);
              const soldOut = rowSoldOut(r);
              // A returned (closed) pack: red row, inputs disabled, sold-out checkbox
              // hidden, End frozen at its close number, inline Restore within 3 days.
              const returned = !!r.isReturned;
              const rowBg = returned
                ? COLORS.rowReturned
                : (edited ? COLORS.editedRow : (idx % 2 === 0 ? COLORS.rowDark : COLORS.rowLight));
              const rowContent = (
                <Box
                  component='tr'
                  key={r.inventoryId}
                  sx={{
                    backgroundColor: rowBg,
                    boxShadow: highlightId === r.inventoryId ? `inset 0 0 0 2px ${COLORS.blue}` : "none",
                  }}
                >
                  <Box component='td' sx={tdSx}>
                    {r.imageUrl ? <Box component='img' src={r.imageUrl} alt={r.name} sx={{ height: "34px", width: "auto" }} /> : "-"}
                  </Box>
                  <Box component='td' sx={tdSx}>{r.name}</Box>
                  <Box component='td' sx={tdSx}>{r.gameNumber}</Box>
                  <Box component='td' sx={tdSx}>{r.packNumber}</Box>
                  <Box component='td' sx={tdSx}>{r.packSize}</Box>
                  <Box component='td' sx={tdSx}>
                    {canEdit && isCurrent && !returned ? (
                      <Box component='input' type='number' value={rowSlotValue(r)} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEdit(r.inventoryId, "slotNumber", e.target.value)} sx={cellInputSx} />
                    ) : (
                      rowSlotValue(r)
                    )}
                  </Box>
                  <Box component='td' sx={tdSx}>{String(r.startNumber).padStart(3, "0")}</Box>
                  <Box component='td' sx={{ ...tdSx, backgroundColor: soldOut ? COLORS.soldOutCell : "transparent" }}>
                    {returned ? (
                      <Box display='flex' alignItems='center' justifyContent='center' gap='6px'>
                        <span>{r.endNumber}</span>
                        {withinRestoreWindow(r.returnedDate) && (
                          <Button
                            onClick={() => setRestoreTarget(r)}
                            disableElevation
                            sx={{ backgroundColor: COLORS.navy, color: "#fff", textTransform: "none", borderRadius: "4px", height: "26px", fontSize: "13px", fontWeight: 600, px: 1, minWidth: 0, "&:hover": { backgroundColor: COLORS.navy, opacity: 0.9 } }}
                          >
                            {t("restoreButton", "Restore")}
                          </Button>
                        )}
                      </Box>
                    ) : canEdit && !soldOut ? (
                      <Box
                        component='input'
                        type='number'
                        ref={(el: HTMLInputElement | null) => { endRefs.current[r.inventoryId] = el; }}
                        value={rowEndValue(r)}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEdit(r.inventoryId, "endNumber", e.target.value)}
                        sx={cellInputSx}
                      />
                    ) : (
                      soldOut ? r.packSize : rowEndValue(r)
                    )}
                  </Box>
                  <Box component='td' sx={tdSx}>
                    {/* the sold-out checkbox is hidden for a returned pack */}
                    {returned ? null : (
                      <Checkbox
                        size='small'
                        checked={soldOut}
                        disabled={!canEdit || !isCurrent}
                        onChange={(e) => setEdit(r.inventoryId, "isSoldOut", e.target.checked)}
                      />
                    )}
                  </Box>
                  <Box component='td' sx={tdSx}>{rowSold(r)}</Box>
                  <Box component='td' sx={tdSx}>{money(parseFloat(r.ticketValue) || 0)}</Box>
                  <Box component='td' sx={tdSx}>{money(rowAmount(r))}</Box>
                </Box>
              );
              return returned ? (
                <Tooltip key={r.inventoryId} title={t("packReturnedTooltip", "Pack returned")} arrow placement='top'>
                  {rowContent}
                </Tooltip>
              ) : (
                rowContent
              );
            })}
            {!rows.length && (
              <Box component='tr'>
                <Box component='td' colSpan={12} sx={{ ...tdSx, py: 3, color: "#777" }}>
                  {t("noSalesRows", "No active tickets for this day")}
                </Box>
              </Box>
            )}
          </Box>
          {rows.length > 0 && (
            <Box component='tfoot'>
              <Box component='tr' sx={{ backgroundColor: COLORS.panelBg }}>
                <Box component='td' colSpan={9} sx={{ ...tdSx, textAlign: "right", fontWeight: 700, pr: 2 }}>
                  {t("totalTicketsSold", "Total Tickets Sold")}: {totalTicketsSold}
                </Box>
                <Box component='td' colSpan={3} sx={{ ...tdSx, textAlign: "left", fontWeight: 700 }}>
                  {t("totalSold", "Total Sold")}: {money(totalAmount)}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Business date + Save */}
      <Box display='flex' alignItems='center' justifyContent='space-between' flexWrap='wrap' gap='12px' mt={1.5}>
        <Typography sx={{ fontSize: "15px", color: "#434343" }}>
          {t("businessDate", "Business Date")}: {formatSlash(meta.currentBusinessDate)}
        </Typography>
        <Button onClick={handleSave} disabled={saving || !canEdit} disableElevation sx={topBtn(COLORS.blue)}>
          {t("saveButton", "SAVE")}
        </Button>
      </Box>

      {/* General information help block (Figma) */}
      <Box sx={{ mt: 2, backgroundColor: COLORS.panelBg, border: `0.25px solid ${COLORS.border}`, borderRadius: "4px", p: 2 }}>
        <Typography sx={{ fontSize: "14px", fontWeight: 700, mb: 0.5 }}>{t("generalInformation", "General Information")}</Typography>
        <Box component='ul' sx={{ m: 0, pl: 3, fontSize: "13px", color: "#434343", lineHeight: 1.6 }}>
          <li>{t("saleHelp1", "Tickets can only be sold for the selected Business Date.")}</li>
          <li>{t("saleHelp2", "Records can be updated for up to 10 previous days from the Business Date.")}</li>
          <li>{t("saleHelp3", "Enter or scan the Game Number and Pack Number to load a ticket, then enter its Ending Number.")}</li>
          <li>{t("saleHelp4", "A row turns green once it is updated. End Day is enabled only after every ticket is updated.")}</li>
        </Box>
      </Box>

      <ConfirmActivateModal
        open={confirmActivateOpen}
        onClose={() => setConfirmActivateOpen(false)}
        socket={socket}
        apiBaseURL={apiBaseURL}
        shopId={shopId}
        onConfirmedActivated={refresh}
      />

      {/* End Day confirmation */}
      <Dialog open={endDayOpen} onClose={() => setEndDayOpen(false)} PaperProps={{ sx: { borderRadius: "10px", p: 1 } }}>
        <Box sx={{ p: 3, maxWidth: "420px" }}>
          <Typography sx={{ fontSize: "18px", fontWeight: 700, mb: 1 }}>{t("endDayConfirmTitle", "End the day?")}</Typography>
          <Typography sx={{ fontSize: "15px", color: "#434343", mb: 2 }}>
            {t("endDayConfirmBody", "This will finalize all sales for this date. Tomorrow's starting numbers will be set from today's ending numbers. This cannot be undone.")}
          </Typography>
          <Box display='flex' justifyContent='flex-end' gap='12px'>
            <Button onClick={() => setEndDayOpen(false)} disableElevation sx={topBtn(COLORS.grey)}>{t("cancel", "Cancel")}</Button>
            <Button onClick={handleEndDay} disableElevation sx={topBtn(COLORS.red)}>{t("endDay", "End Day")}</Button>
          </Box>
        </Box>
      </Dialog>

      {/* Restore (un-return) confirmation */}
      <Dialog open={!!restoreTarget} onClose={() => setRestoreTarget(null)} PaperProps={{ sx: { borderRadius: "10px", p: 1 } }}>
        <Box sx={{ p: 3, maxWidth: "420px" }}>
          <Typography sx={{ fontSize: "18px", fontWeight: 700, mb: 1 }}>{t("restoreConfirmTitle", "Restore this pack?")}</Typography>
          <Typography sx={{ fontSize: "15px", color: "#434343", mb: 2 }}>
            {t("restoreConfirmBody", "This will restore the pack to its previous status. If it was active, it will be placed back on its slot.")}
          </Typography>
          <Box display='flex' justifyContent='flex-end' gap='12px'>
            <Button onClick={() => setRestoreTarget(null)} disableElevation sx={topBtn(COLORS.grey)}>{t("cancel", "Cancel")}</Button>
            <Button onClick={handleRestore} disabled={restoring} disableElevation sx={topBtn(COLORS.navy)}>{t("restoreButton", "Restore")}</Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default SaleDayDetail;
